// Mirrored in applications/platform-app/components/web-analytics — change both.
import { createContext, useContext } from 'react';
import type { WebAnalyticsApi } from './api';
import type { SiteKind, SiteRef, WebDim, WebFilter, Incident, SummaryResponse } from './types';
import type { WebQueryResult } from './useWebQuery';
import type { DashboardState, TabId } from './useDashboardState';
import type { MetricId } from './format';

export interface BlockPrefill { kind?: 'ip' | 'net24' | 'country' | 'bot'; target?: string }

/** Everything a tab needs — one object instead of a dozen props per tab. */
export interface DashCtx {
  api: WebAnalyticsApi;
  mode: 'admin' | 'tenant';
  siteId: string;
  site: SiteRef | undefined;
  siteKind: SiteKind;
  sites: SiteRef[];
  state: DashboardState;
  filtered: boolean;
  /** One summary query for the header (live, threat badge) and the Overview KPIs. */
  summary: WebQueryResult<SummaryResponse>;
  setSite: (id: string) => void;
  setTab: (t: TabId) => void;
  setMetric: (m: MetricId) => void;
  addFilter: (f: WebFilter) => void;
  /** Click handler for a breakdown row of `dim`, or undefined when that dim is not filterable. */
  rowFilter: (dim: WebDim, value: string) => (() => void) | undefined;
  toast: (t: string) => void;
  openBlock: (p?: BlockPrefill) => void;
  openIncident: (i: Incident) => void;
  openViewAll: (dim: WebDim, title: string) => void;
  /** Bumped after a block/unblock so the Security tab refetches. */
  blocksNonce: number;
  bumpBlocks: () => void;
  /** Realtime tab reports its live count so the header pill stays current. */
  setLive: (n: number) => void;
  /** Each query reports its loading state; any in flight dims the body under the ruby gem. */
  reportLoading: (id: string, on: boolean) => void;
}

export const DashContext = createContext<DashCtx | null>(null);
export function useDash(): DashCtx {
  const c = useContext(DashContext);
  if (!c) throw new Error('useDash outside WebAnalyticsDashboard');
  return c;
}
